<template>
    <Transition name="fade">
        <div v-if="!isTransitioning && !showWidgetList" class="top-10 right-7 absolute">
            <div class="flex items-center justify-end gap-2">
                <template v-if="showEndRolePlay">
                    <BaseButton
                        :disabled="!isEndRolePlayEnabled"
                        theme="primary"
                        class="whitespace-nowrap animate-shadow-pulse"
                        data-testid="role-play-recap-button"
                        @click="emitter.emit('end_role_play')">
                        Create Role Play Recap
                    </BaseButton>
                </template>
                <BaseButton v-else-if="showWrapUp" theme="primary" class="whitespace-nowrap animate-shadow-pulse" @click="emitter.emit(CHAT_EVENT.WRAP_UP_CHAT)">
                    Wrap Up
                </BaseButton>
                <ChatWidgetListToggle :visible-widget-count="visibleWidgetCount" :is-open="showWidgetList" @click="$emit('toggleShowWidgetList')" />
            </div>

            <ChatCalendarEvent
                v-if="calendarEvent"
                class="mt-2"
                :start-time="calendarEvent.start_time"
                :end-time="calendarEvent.end_time"
                :attendees="calendarEvent.attendees"
                :subject="calendarEvent.subject" />
        </div>
    </Transition>
    <!-- FIXME: use BaseSidePanel once it supports inline notes panels with coordinated control transitions. -->
    <Transition name="slide" @before-leave="transitionStart" @after-leave="transitionEnd" @before-enter="transitionStart" @after-enter="transitionEnd">
        <div v-show="showWidgetList" class="grow flex-col w-100 flex bg-surface-raised border-l-2 border-l-border max-h-full overflow-y-hidden">
            <ChatWidgetList
                :visible-widget-count="visibleWidgetCount"
                :role-play-adjustment-default="rolePlayAdjustmentDefault"
                :role-play-adjustments="rolePlayAdjustments"
                :messages="messages"
                :is-open="showWidgetList"
                :is-end-role-play-enabled="isEndRolePlayEnabled"
                :show-wrap-up="showWrapUp"
                :show-end-role-play="showEndRolePlay"
                :hide-email-button="hideEmailButton"
                :nadia-notes-enabled="nadiaNotesEnabled"
                @expand="$emit('toggleShowWidgetList')"
                @close="$emit('toggleShowWidgetList')" />
        </div>
    </Transition>
</template>

<script setup lang="ts">
import type { Emitter } from "mitt";
import { inject } from "vue";

import ChatCalendarEvent from "./ChatDetail/ChatCalendarEvent.vue";
import ChatWidgetList from "./ChatWidgetList.vue";
import ChatWidgetListToggle from "./ChatWidgetListToggle.vue";
import BaseButton from "./components/BaseButton.vue";
import { CHAT_EVENT } from "./events";
import { type ChatMessage, type RolePlayAdjustment, RolePlayAdjustmentEnum, type RolePlayAdjustmentProp } from "./types/chat";

interface CalendarEventData {
    subject: string;
    start_time: string;
    end_time: string;
    attendees?: Array<{ name: string; email: string }>;
}

interface Props {
    isTransitioning?: boolean;
    rolePlayAdjustmentDefault?: RolePlayAdjustment;
    rolePlayAdjustments?: RolePlayAdjustmentProp;
    messages?: ChatMessage[];
    showWidgetList?: boolean;
    showWrapUp?: boolean;
    visibleWidgetCount?: number;
    showEndRolePlay?: boolean;
    isEndRolePlayEnabled?: boolean;
    hasFeedbackReport?: boolean;
    calendarEvent?: CalendarEventData;
    hideEmailButton?: boolean;
    nadiaNotesEnabled?: boolean;
}

withDefaults(defineProps<Props>(), {
    isTransitioning: false,
    rolePlayAdjustmentDefault: RolePlayAdjustmentEnum.IS_OK,
    rolePlayAdjustments: () => [],
    messages: () => [],
    showWidgetList: true,
    showWrapUp: false,
    visibleWidgetCount: 0,
    showEndRolePlay: false,
    isEndRolePlayEnabled: false,
    hasFeedbackReport: false,
    calendarEvent: undefined,
    hideEmailButton: false,
    nadiaNotesEnabled: false,
});

const { emitter } = inject("globalProperties") as { emitter: Emitter<Record<string, unknown>> };

const emit = defineEmits<{
    toggleShowWidgetList: [];
    transitionStart: [];
    transitionEnd: [];
}>();

const transitionStart = () => emit("transitionStart");
const transitionEnd = () => emit("transitionEnd");
</script>

<style scoped>
.slide-leave-active,
.slide-enter-active {
    transition: transform var(--motion-duration-slow) var(--motion-ease-enter);
}

.slide-leave-from,
.slide-enter-to {
    transform: translateX(0);
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(100%);
}

/*
 * The controls outside the sidebar fade in and out in sync
 * with the sidebar. However, a delay is added when closing
 * so that the controls appears slightly after the
 * sidebar settles. In the other hand, the controls
 * fade out quickly when opening. Hence why two
 * similar but different fade classes are used.
 */

.fade-close-enter-active,
.fade-close-leave-active,
.fade-enter-active {
    transition: opacity 200ms ease-out;
}

.fade-leave-active {
    transition: opacity 50ms ease-in;
}

.fade-close-enter-active,
.fade-enter-active {
    transition-delay: 100ms;
}

.fade-close-enter-from,
.fade-close-leave-to,
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .slide-leave-active,
    .slide-enter-active {
        transition: none;
    }

    .slide-enter-from,
    .slide-leave-to {
        transform: none;
    }
}
</style>
